import clsx from 'clsx';
import { AlertTriangle, Check, CloudOff, RefreshCw, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

import { flush, getStatus, subscribe, type SyncStatus } from '../lib/offline/sync';
import { relativeTime } from '../lib/format';
import { Modal, Button } from './ui';

/**
 * Sync status (directive §35).
 *
 * Always shows one of: Synced / Pending / Offline / Sync error — never a silent
 * failure. The operator has to be able to tell, at a glance, whether their work
 * has left the device.
 */
export function SyncIndicator() {
  const [status, setStatus] = useState<SyncStatus>(getStatus);
  const [open, setOpen] = useState(false);

  useEffect(() => subscribe(setStatus), []);

  const { tone, Icon, label, spin } = describe(status);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={clsx(
          'flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold',
          tone === 'ok' && 'border-ok-500/40 bg-ok-50 text-ok-700',
          tone === 'warn' && 'border-warn-500/40 bg-warn-50 text-warn-700',
          tone === 'alarm' && 'border-alarm-500/40 bg-alarm-50 text-alarm-700',
          tone === 'neutral' && 'border-steel-300 bg-steel-50 text-steel-600',
        )}
      >
        <Icon aria-hidden className={clsx('size-4', spin && 'animate-spin')} />
        <span className="hidden sm:inline">{label}</span>
        {status.pending > 0 ? (
          <span className="tabular-nums">{status.pending}</span>
        ) : null}
        <span className="sr-only">Sync status: {label}</span>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Sync status"
        footer={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
            <Button
              fullWidth
              icon={RefreshCw}
              disabled={!status.online || status.state === 'SYNCING'}
              onClick={() => void flush()}
            >
              Sync now
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm">
          <div
            className={clsx(
              'flex items-center gap-3 rounded-lg border p-3',
              tone === 'ok' && 'border-ok-500/40 bg-ok-50 text-ok-700',
              tone === 'warn' && 'border-warn-500/40 bg-warn-50 text-warn-700',
              tone === 'alarm' && 'border-alarm-500/40 bg-alarm-50 text-alarm-700',
              tone === 'neutral' && 'border-steel-300 bg-steel-50 text-steel-700',
            )}
          >
            <Icon aria-hidden className={clsx('size-6 shrink-0', spin && 'animate-spin')} />
            <div>
              <p className="font-bold">{label}</p>
              <p className="mt-0.5 text-xs">{explain(status)}</p>
            </div>
          </div>

          <dl className="rounded-lg border border-steel-200">
            <Row label="Connection">{status.online ? 'Online' : 'Offline'}</Row>
            <Row label="Waiting to send">
              {status.pending === 0 ? 'Nothing — everything is saved on the server' : `${status.pending} item(s)`}
            </Row>
            <Row label="Needs attention">
              {status.conflicts === 0 ? 'None' : `${status.conflicts} conflict(s)`}
            </Row>
            <Row label="Last synced">
              {status.lastSyncedAt ? relativeTime(status.lastSyncedAt) : 'Not yet this session'}
            </Row>
          </dl>

          {status.lastError ? (
            <div className="rounded-lg border border-warn-500/40 bg-warn-50 p-3 text-warn-700">
              <p className="text-xs font-bold uppercase">Last message</p>
              <p className="mt-1">{status.lastError}</p>
            </div>
          ) : null}

          <p className="text-xs text-steel-500">
            Your checklist answers, photos and problem tags are stored on this device first,
            then sent to the server. Nothing is lost if you go out of coverage — you can keep
            working and it will send itself when the connection returns.
          </p>
        </div>
      </Modal>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-steel-100 px-3 py-2 last:border-0">
      <dt className="text-xs font-semibold text-steel-500 uppercase">{label}</dt>
      <dd className="text-right text-sm font-medium text-steel-900">{children}</dd>
    </div>
  );
}

function describe(status: SyncStatus) {
  if (!status.online) {
    return { tone: 'warn' as const, Icon: CloudOff, label: 'Offline', spin: false };
  }
  if (status.conflicts > 0) {
    return { tone: 'alarm' as const, Icon: TriangleAlert, label: 'Needs attention', spin: false };
  }
  if (status.state === 'SYNCING') {
    return { tone: 'neutral' as const, Icon: RefreshCw, label: 'Syncing', spin: true };
  }
  if (status.state === 'ERROR') {
    return { tone: 'alarm' as const, Icon: AlertTriangle, label: 'Sync error', spin: false };
  }
  if (status.pending > 0) {
    return { tone: 'warn' as const, Icon: RefreshCw, label: 'Pending sync', spin: false };
  }
  return { tone: 'ok' as const, Icon: Check, label: 'Synced', spin: false };
}

function explain(status: SyncStatus): string {
  if (!status.online) {
    return 'You can keep working. Everything is saved on this device and will send itself when you are back in coverage.';
  }
  if (status.conflicts > 0) {
    return 'Some changes could not be applied because the record moved on while you were offline. Open the item to see what happened.';
  }
  if (status.state === 'SYNCING') return 'Sending your work to the server now.';
  if (status.state === 'ERROR') return 'The last attempt failed. It will retry automatically.';
  if (status.pending > 0) return 'Work is queued and will send shortly.';
  return 'All your work is saved on the server.';
}

/** Full-width banner shown at the top of operational screens while offline. */
export function OfflineBanner() {
  const [status, setStatus] = useState<SyncStatus>(getStatus);
  useEffect(() => subscribe(setStatus), []);

  if (status.online && status.conflicts === 0) return null;

  if (!status.online) {
    return (
      <div
        role="status"
        className="flex items-center gap-2 border-b border-warn-500/40 bg-warn-50 px-4 py-2 text-sm font-semibold text-warn-700"
      >
        <CloudOff aria-hidden className="size-4 shrink-0" />
        <span>
          Offline — you can carry on. Your work is saved on this device
          {status.pending > 0 ? ` (${status.pending} waiting to send)` : ''}.
        </span>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="flex items-center gap-2 border-b border-alarm-500/40 bg-alarm-50 px-4 py-2 text-sm font-semibold text-alarm-700"
    >
      <TriangleAlert aria-hidden className="size-4 shrink-0" />
      <span>
        {status.conflicts} change(s) could not be applied. Tap the sync badge for details.
      </span>
    </div>
  );
}
